import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileCsv, FileXls, FilePdf, Export, Funnel } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";

const DATA_TYPES = [
  { value: "validations", label: "Validations", hasDate: true, adminOnly: false },
  { value: "flagged", label: "Flagged Cases", hasDate: true, adminOnly: false },
  { value: "audit_logs", label: "Audit Logs", hasDate: true, adminOnly: true },
  { value: "analytics", label: "Analytics Summary", hasDate: false, adminOnly: false },
  { value: "car_profiles", label: "Car Profiles", hasDate: false, adminOnly: false },
  { value: "users", label: "Users", hasDate: false, adminOnly: true },
];

function FormatButton({ format, icon: Icon, color, label, onClick, disabled }) {
  return (
    <Button onClick={onClick} disabled={disabled}
      className="flex items-center gap-2 rounded-sm h-11 text-sm font-semibold transition-all"
      style={{ backgroundColor: disabled ? "#333" : color, color: "#000" }} data-testid={`export-${format}-btn`}>
      <Icon size={20} weight="bold" />{disabled ? "Exporting..." : label}
    </Button>
  );
}

export function ExportTab() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin";
  const availableTypes = DATA_TYPES.filter((dt) => !dt.adminOnly || isSuperadmin);
  const [dataType, setDataType] = useState("validations");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [exporting, setExporting] = useState(false);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.set("data_type", dataType);
      if (startDate) params.set("start_date", new Date(startDate).toISOString());
      if (endDate) params.set("end_date", new Date(endDate + "T23:59:59").toISOString());

      const response = await api.get(`/export/${format}?${params.toString()}`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data]);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${dataType}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success(`${format.toUpperCase()} exported`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const selectedType = availableTypes.find((d) => d.value === dataType);

  return (
    <div className="space-y-6 max-w-3xl">
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Funnel size={16} weight="bold" className="text-[#007AFF]" />Export Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Data Type</Label>
            <Select value={dataType} onValueChange={(v) => { setDataType(v); setStartDate(""); setEndDate(""); }}>
              <SelectTrigger className="w-full bg-[#0A0A0A] border-[#262626] text-white rounded-sm" data-testid="export-data-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#262626]">
                {availableTypes.map((dt) => (
                  <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedType?.hasDate && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">Start Date <span className="normal-case text-[#737373]">(optional)</span></Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="export-start-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">End Date <span className="normal-case text-[#737373]">(optional)</span></Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data" data-testid="export-end-date" />
              </div>
            </div>
          )}
          {(startDate || endDate) && (
            <button onClick={() => { setStartDate(""); setEndDate(""); }}
              className="text-xs text-[#007AFF] hover:underline" data-testid="export-clear-dates">
              Clear date filters
            </button>
          )}
        </CardContent>
      </Card>
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Export size={16} weight="bold" className="text-[#007AFF]" />Export Format
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-[#737373] mb-4">
            Exporting <span className="text-white font-semibold">{selectedType?.label}</span>
            {startDate && endDate ? ` from ${startDate} to ${endDate}` : startDate ? ` from ${startDate}` : endDate ? ` until ${endDate}` : " (all records)"}
          </p>
          <div className="grid grid-cols-3 gap-3">
            <FormatButton format="csv" icon={FileCsv} color="#32D74B" label="CSV" onClick={() => handleExport("csv")} disabled={exporting} />
            <FormatButton format="xlsx" icon={FileXls} color="#007AFF" label="XLSX" onClick={() => handleExport("xlsx")} disabled={exporting} />
            <FormatButton format="pdf" icon={FilePdf} color="#FF3B30" label="PDF" onClick={() => handleExport("pdf")} disabled={exporting} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
